import { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  ArrowLeft,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  User,
  Server,
  FileText,
  AlertTriangle,
  Volume2,
  Eye,
  Download,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
} from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { parseCompoundLanguageCode } from '../../utils/language-code';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/core/components/ui/card';
import { Label } from '@/core/components/ui/label';
import { Textarea } from '@/core/components/ui/textarea';
import { Badge } from '@/core/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/core/components/ui/dialog';
import { Skeleton } from '@/core/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/core/components/ui/collapsible';
import { ApplicationTypeSelect } from '../atoms/ApplicationTypeSelect';
import { MissingTranslationsModal } from '../organisms/MissingTranslationsModal';
import { usePublishStore } from '../../stores/publish-store';
import { useTranslationStore } from '../../stores/translation-store';
import { useSoundManagementStore } from '../../stores/sound-management-store';
import { copyToClipboard } from '@/core/utils/clipboard';
import {
  ApplicationType,
  ApplicationTypeLabels,
  S3Environment,
  S3EnvironmentLabels,
  PreviewFileInfo,
} from '../../types/translation-types';
import { supportsSounds } from '../../types/sound-management-types';
import { toast } from 'sonner';
import { appConfig } from '@/core/config';

export function PublishPage() {
  const navigate = useNavigate();

  // Use shared app type from translation store
  const { selectedAppType, setSelectedAppType } = useTranslationStore();

  const {
    publishStatus,
    isLoading,
    isPublishing,
    error,
    publishError,
    fetchPublishStatus,
    publish,
    setSelectedAppType: setPublishSelectedAppType,
    clearPublishError,
    // Preview state
    previewInfo,
    previewContent,
    selectedPreviewFile,
    isLoadingPreviewInfo,
    isLoadingPreviewContent,
    isDownloadingPreview,
    previewError,
    // Preview actions
    fetchPreviewInfo,
    fetchPreviewContent,
    downloadPreview,
    setSelectedPreviewFile,
    clearPreviewError,
    // Missing keys modal state
    missingKeysModalOpen,
    selectedMissingLanguage,
    missingKeys,
    isLoadingMissingKeys,
    // Missing keys modal actions
    openMissingKeysModal,
    closeMissingKeysModal,
  } = usePublishStore();

  // Sound store for showing sound count
  const {
    totalCount: soundCount,
    isLoading: isSoundsLoading,
    fetchSounds,
    setApplicationType: setSoundAppType,
  } = useSoundManagementStore();

  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState<S3Environment>(S3Environment.QA);
  const [publishNotes, setPublishNotes] = useState('');
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);
  const [copiedJsonPreview, setCopiedJsonPreview] = useState(false);

  // Check if current app type supports sounds
  const appSupportsSounds = selectedAppType !== null && supportsSounds(selectedAppType);

  // Sync publish store app type and fetch status when app type changes
  useEffect(() => {
    if (selectedAppType !== null) {
      setPublishSelectedAppType(selectedAppType);
      fetchPublishStatus();
    }
  }, [fetchPublishStatus, selectedAppType, setPublishSelectedAppType]);

  // Fetch sound count when app type changes and supports sounds
  useEffect(() => {
    if (selectedAppType !== null && supportsSounds(selectedAppType)) {
      setSoundAppType(selectedAppType);
      fetchSounds();
    }
  }, [selectedAppType, setSoundAppType, fetchSounds]);

  const handleAppTypeChange = useCallback((appType: ApplicationType | null) => {
    setSelectedAppType(appType);
    if (appType !== null) {
      setPublishSelectedAppType(appType);
    }
  }, [setSelectedAppType, setPublishSelectedAppType]);

  const handleOpenPublishDialog = useCallback((environment: S3Environment) => {
    setSelectedEnvironment(environment);
    setPublishNotes('');
    clearPublishError();
    setPublishDialogOpen(true);
  }, [clearPublishError]);

  const handlePublish = useCallback(async () => {
    try {
      const result = await publish(selectedEnvironment, publishNotes || undefined);
      setPublishDialogOpen(false);
      toast.success(`Published v${result.version} to ${S3EnvironmentLabels[selectedEnvironment]}`);
    } catch {
      // Error is handled by store
    }
  }, [publish, selectedEnvironment, publishNotes]);

  // Preview handlers
  const handleExpandPreview = useCallback(async () => {
    const newExpanded = !isPreviewExpanded;
    setIsPreviewExpanded(newExpanded);

    // Fetch preview info when expanding for the first time
    if (newExpanded && !previewInfo && !isLoadingPreviewInfo) {
      await fetchPreviewInfo();
    }
  }, [isPreviewExpanded, previewInfo, isLoadingPreviewInfo, fetchPreviewInfo]);

  const handlePreviewFileChange = useCallback((fileKey: string) => {
    const file = previewInfo?.availableFiles.find(f => f.fileKey === fileKey) || null;
    setSelectedPreviewFile(file);
  }, [previewInfo, setSelectedPreviewFile]);

  const handlePreviewClick = useCallback(async () => {
    clearPreviewError();
    const fileKey = previewInfo?.isMultiFile ? selectedPreviewFile?.fileKey : undefined;
    await fetchPreviewContent(fileKey);
  }, [previewInfo, selectedPreviewFile, fetchPreviewContent, clearPreviewError]);

  const handleDownloadClick = useCallback(async () => {
    clearPreviewError();
    const fileKey = previewInfo?.isMultiFile ? selectedPreviewFile?.fileKey : undefined;
    const fileName = selectedPreviewFile?.fileName || `${ApplicationTypeLabels[selectedAppType]}.json`;
    await downloadPreview(fileName, fileKey);
    toast.success(`Downloaded ${fileName}`);
  }, [previewInfo, selectedPreviewFile, selectedAppType, downloadPreview, clearPreviewError]);

  const handleCopyJsonPreview = useCallback(async () => {
    if (previewContent) {
      // Format JSON the same way as the preview display
      const jsonString = (() => {
        try {
          return JSON.stringify(JSON.parse(previewContent), null, 2);
        } catch {
          return previewContent;
        }
      })();

      // Copy to clipboard with success toast
      const success = await copyToClipboard(
        jsonString,
        'JSON copied to clipboard',
        'Failed to copy JSON'
      );

      // Show visual feedback
      if (success) {
        setCopiedJsonPreview(true);
        setTimeout(() => setCopiedJsonPreview(false), 1500);
      }
    }
  }, [previewContent]);

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const renderEnvironmentPublishInfo = (
    environment: 'QA' | 'Production',
    publishInfo: typeof publishStatus.lastPublishedQa
  ) => {
    if (!publishInfo) {
      return (
        <div className="text-sm text-muted-foreground italic">
          Not published to {environment} yet
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">Version:</span>
          <span className="font-medium">v{publishInfo.version}</span>
          {publishInfo.keyCount && (
            <span className="text-muted-foreground">({publishInfo.keyCount} keys)</span>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">Published:</span>
          <span className="font-medium">{formatDateTime(publishInfo.publishedAt)}</span>
        </div>
        {publishInfo.publishedBy && (
          <div className="flex items-center gap-2 text-sm">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">By:</span>
            <span className="font-medium">{publishInfo.publishedBy}</span>
          </div>
        )}
        {publishInfo.notes && (
          <div className="text-sm text-muted-foreground mt-1 italic">
            "{publishInfo.notes}"
          </div>
        )}
      </div>
    );
  };

  // Each back-office only publishes to its own environment; dev/local can publish to both
  const currentEnv = appConfig.getRuntimeConfig()?.environment ?? appConfig.getEnvironment();
  const showQa = currentEnv !== 'prod';
  const showProd = currentEnv !== 'qa';

  const renderPublishCard = () => {
    if (!publishStatus) return null;

    return (
      <div className={`grid grid-cols-1 gap-4 ${showQa && showProd ? 'md:grid-cols-2' : ''}`}>
        {/* QA Environment */}
        {showQa && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Server className="h-5 w-5" />
                  QA Environment
                </CardTitle>
                {publishStatus.lastPublishedQa && (
                  <Badge className="bg-blue-100 text-blue-800">v{publishStatus.lastPublishedQa.version}</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {renderEnvironmentPublishInfo('QA', publishStatus.lastPublishedQa)}
              <Button
                className="w-full"
                onClick={() => handleOpenPublishDialog(S3Environment.QA)}
              >
                <Upload className="h-4 w-4 mr-2" />
                Publish to QA
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Production Environment */}
        {showProd && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Server className="h-5 w-5" />
                  Production Environment
                </CardTitle>
                {publishStatus.lastPublishedProd && (
                  <Badge className="bg-green-100 text-green-800">v{publishStatus.lastPublishedProd.version}</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {renderEnvironmentPublishInfo('Production', publishStatus.lastPublishedProd)}
              <Button
                className="w-full"
                variant="outline"
                onClick={() => handleOpenPublishDialog(S3Environment.Production)}
              >
                <Upload className="h-4 w-4 mr-2" />
                Publish to Production
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderMissingTranslationsCard = () => {
    if (!publishStatus) return null;

    const hasMissing = publishStatus.missingTranslations && publishStatus.missingTranslations.length > 0;
    const totalMissingKeys = hasMissing
      ? publishStatus.missingTranslations.reduce((sum, item) => sum + item.count, 0)
      : 0;

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Translation Coverage
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasMissing ? (
            <div className="space-y-3">
              <div className="text-center p-4 rounded-lg bg-yellow-50">
                <div className="text-2xl font-bold text-yellow-600">
                  {publishStatus.missingTranslations.length}
                </div>
                <div className="text-sm text-yellow-700">Languages with missing translations</div>
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {publishStatus.missingTranslations.map((item) => (
                  <div
                    key={item.languageCode}
                    className="text-sm flex justify-between items-center py-2 px-2 border-b cursor-pointer hover:bg-muted/50 transition-colors rounded-md"
                    onClick={() => openMissingKeysModal(item.languageCode, item.languageName)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openMissingKeysModal(item.languageCode, item.languageName);
                      }
                    }}
                  >
                    <span className="font-medium">
                      {item.languageName}
                      {parseCompoundLanguageCode(item.languageCode).operatorName && (
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({parseCompoundLanguageCode(item.languageCode).operatorName})
                        </span>
                      )}
                    </span>
                    <Badge variant={item.count === publishStatus.totalKeys ? 'destructive' : 'secondary'}>
                      {item.count} missing
                    </Badge>
                  </div>
                ))}
              </div>
              <div className="text-xs text-muted-foreground text-center pt-2">
                Total: {totalMissingKeys} missing translations across {publishStatus.missingTranslations.length} languages
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              <span>All keys have complete translations ({publishStatus.languageCount} languages)</span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderSkeleton = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    </div>
  );

  const renderPreviewCard = () => {
    return (
      <Card>
        <Collapsible open={isPreviewExpanded} onOpenChange={handleExpandPreview}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Preview Before Publishing
                </CardTitle>
                {isPreviewExpanded ? (
                  <ChevronUp className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
            </CardHeader>
          </CollapsibleTrigger>

          <CollapsibleContent>
            <CardContent className="space-y-4">
              {/* Preview Error */}
              {previewError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{previewError}</AlertDescription>
                </Alert>
              )}

              {/* Loading preview info */}
              {isLoadingPreviewInfo && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  <span className="text-muted-foreground">Loading preview info...</span>
                </div>
              )}

              {/* Preview info loaded */}
              {previewInfo && !isLoadingPreviewInfo && (
                <>
                  {/* File selector for multi-file apps */}
                  {previewInfo.isMultiFile && previewInfo.availableFiles.length > 0 && (
                    <div className="space-y-2">
                      <Label>Select Language File</Label>
                      <Select
                        value={selectedPreviewFile?.fileKey || ''}
                        onValueChange={handlePreviewFileChange}
                      >
                        <SelectTrigger className="w-[300px]">
                          <SelectValue placeholder="Select a language file" />
                        </SelectTrigger>
                        <SelectContent>
                          {previewInfo.availableFiles.map((file: PreviewFileInfo) => (
                            <SelectItem key={file.fileKey} value={file.fileKey}>
                              {file.languageName} ({file.fileName})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Total keys info */}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <FileText className="h-4 w-4" />
                    <span>Total Keys: <strong className="text-foreground">{previewInfo.totalKeys}</strong></span>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handlePreviewClick}
                      disabled={isLoadingPreviewContent || (previewInfo.isMultiFile && !selectedPreviewFile)}
                    >
                      {isLoadingPreviewContent ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <Eye className="h-4 w-4 mr-2" />
                          Preview
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleDownloadClick}
                      disabled={isDownloadingPreview || (previewInfo.isMultiFile && !selectedPreviewFile)}
                    >
                      {isDownloadingPreview ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Downloading...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </>
                      )}
                    </Button>
                  </div>

                  {/* JSON Preview content */}
                  {previewContent && (
                    <div className="mt-4">
                      {/* Header with Label and Copy Button */}
                      <div className="flex items-center justify-between mb-2">
                        <Label>JSON Preview</Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleCopyJsonPreview}
                          className="h-7 px-2 hover:bg-slate-100 rounded flex items-center gap-1.5"
                          title="Copy JSON to clipboard"
                        >
                          {copiedJsonPreview ? (
                            <>
                              <Check className="h-4 w-4 text-green-600" />
                              <span className="text-xs text-green-600 font-medium">Copied!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4 text-slate-600" />
                              <span className="text-xs text-slate-600">Copy</span>
                            </>
                          )}
                        </Button>
                      </div>

                      {/* JSON Preview Code Block */}
                      <div className="bg-slate-900 text-slate-100 rounded-lg p-4 max-h-[400px] overflow-auto">
                        <pre className="text-sm font-mono whitespace-pre-wrap break-words">
                          {(() => {
                            try {
                              return JSON.stringify(JSON.parse(previewContent), null, 2);
                            } catch {
                              return previewContent;
                            }
                          })()}
                        </pre>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    );
  };

  return (
    <div className="container mx-auto py-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Publish Translations</h1>
          <p className="text-muted-foreground">
            {showQa && showProd
              ? 'Publish translations to QA or Production environments'
              : `Publish translations to the ${showProd ? 'Production' : 'QA'} environment`}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate('/translations')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Translations
        </Button>
      </div>

      {/* App Type Selection */}
      <div className="mb-6">
        <ApplicationTypeSelect
          value={selectedAppType}
          onChange={handleAppTypeChange}
          className="w-[300px]"
          required
        />
      </div>

      {/* Error State */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && renderSkeleton()}

      {/* Content */}
      {!isLoading && publishStatus && (
        <div className="space-y-6">
          {/* Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Translation Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`grid gap-4 ${appSupportsSounds ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <div className="text-center p-4 rounded-lg bg-muted">
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <span className="text-2xl font-bold">{publishStatus.totalKeys}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">Translation Keys</div>
                </div>
                {appSupportsSounds && (
                  <div className="text-center p-4 rounded-lg bg-muted">
                    <div className="flex items-center justify-center gap-2">
                      <Volume2 className="h-5 w-5 text-muted-foreground" />
                      {isSoundsLoading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <span className="text-2xl font-bold">{soundCount}</span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">Sound Files</div>
                    <Button
                      variant="link"
                      size="sm"
                      className="mt-1 h-auto p-0 text-xs"
                      onClick={() => navigate('/translations/sounds')}
                    >
                      Manage Sounds →
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Preview Card */}
          {renderPreviewCard()}

          {/* Missing Translations Card */}
          {renderMissingTranslationsCard()}

          {/* Publish Card */}
          {renderPublishCard()}

          {/* Version History Link */}
          <div className="text-center">
            <Button variant="link" onClick={() => navigate('/translations/versions')}>
              View Version History →
            </Button>
          </div>
        </div>
      )}

      {/* Publish Confirmation Dialog */}
      <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Publish to {S3EnvironmentLabels[selectedEnvironment]}
            </DialogTitle>
            <DialogDescription>
              This will create a new version of translations for{' '}
              {ApplicationTypeLabels[selectedAppType]} in the{' '}
              {S3EnvironmentLabels[selectedEnvironment]} environment.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="notes">Release Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Describe the changes in this version..."
                value={publishNotes}
                onChange={(e) => setPublishNotes(e.target.value)}
                rows={3}
              />
            </div>

            {publishError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{publishError}</AlertDescription>
              </Alert>
            )}

            {selectedEnvironment === S3Environment.Production && (
              <Alert className="border-yellow-200 bg-yellow-50">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800">
                  You are publishing to <strong>Production</strong>. These translations will be
                  immediately available to end users.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPublishDialogOpen(false)}
              disabled={isPublishing}
            >
              Cancel
            </Button>
            <Button onClick={handlePublish} disabled={isPublishing}>
              {isPublishing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Publish
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Missing Keys Modal */}
      <MissingTranslationsModal
        isOpen={missingKeysModalOpen}
        languageCode={selectedMissingLanguage?.code || null}
        languageName={selectedMissingLanguage?.name || ''}
        missingKeys={missingKeys}
        isLoading={isLoadingMissingKeys}
        totalKeys={publishStatus?.totalKeys || 0}
        onClose={closeMissingKeysModal}
      />
    </div>
  );
}
